//abstract class for Filter

import {Component} from "htm/preact";
import {FiltersManager} from "./FiltersManager";
import {Context} from "preact";

export interface FilterProps {
    id: string;
    name: string;
    parentFiltersManager?: FiltersManager;
}

export interface State extends Context<any> {
    active: boolean;
    value: any;
}

export abstract class Filter<P = {}, S = State> extends Component<FilterProps, State> {

    readonly id: string;
    readonly name: string;
    readonly filtersManager: FiltersManager | undefined;
    initialStateValue: any;

    lastAppliedValue: any;
    valid: boolean = true;

    abstract cleared: any;

    protected constructor(props: any) {
        super(props);

        this.filtersManager = props.filtersManager;

        if(props){
            this.id = props.id;
            this.name = props.name;
        }
        else{
            this.id = "";
            this.name = "";
        }

        if(props.preloadedState && this.filtersManager?.initialLoad){
            this.state = props.preloadedState;
            // Create a deep copy of the value to prevent reference issues
            const stateValue = JSON.parse(JSON.stringify(props.preloadedState.value || {}));
            this.initialStateValue = stateValue;
            this.lastAppliedValue = JSON.parse(JSON.stringify(stateValue));

        }
        else {
            this.setState({
                active: false,
                value: {}
            });
            this.lastAppliedValue = {};
            this.initialStateValue = {};
        }

        this.reset = this.reset.bind(this);

    }

    setStateValue(val: any, callback?: () => void){
        let active = this.state.active;
        val = JSON.parse(JSON.stringify(val));

        if(JSON.stringify(this.lastAppliedValue) != JSON.stringify(val)){
            this.filtersManager?.toggleApplyButton(true);
        }
        else{
            this.filtersManager?.toggleApplyButton(false);
        }
        if(callback){
            return this.setState({
                active: active,
                value: val},
                () => {
                    this.filtersManager?.notifyFilterChanged(this);
                    callback();
                }
            )
        }else{
            return this.setState({
                active: active,
                value: val},
                () => this.filtersManager?.notifyFilterChanged(this)
            )
        }

    }

    clearStateValue(cb?: () => void){
        let activeTag = document.querySelector("#delta-filters-active-tag_" + this.id);
        let clearButton = document.querySelector("#delta-filters-clear-filter-link_" + this.id);

        if(activeTag){
            clearButton?.classList.add("hidden");
        }

        this.setState({
            active: false,
            value: JSON.parse(JSON.stringify(this.cleared))
        }, () => {
            if(cb){
                cb();
            }
        });
    }

    setActive(active: boolean){
        let activeTag = document.querySelector("#delta-filters-active-tag_" + this.id);
        let clearButton = document.querySelector("#delta-filters-clear-filter-link_" + this.id);

        if(active){
            this.filtersManager?.addActiveFilter(this);
            activeTag?.classList.remove("hidden");
            clearButton?.classList.remove("hidden");
        }
        else{
            this.filtersManager?.removeActiveFilter(this);
            activeTag?.classList.add("hidden");
            clearButton?.classList.add("hidden");
        }

        this.setState({
            active: active,
            value: this.state.value
        })

    }

    addOptionToState(option: any){
        let currentValue = this.state.value;
        if(currentValue?.selected){
            currentValue?.selected?.push(option);
        }
        else{
            currentValue.selected = [option];
        }
        this.setStateValue(currentValue);
    }

    removeOptionFromState(option: any){
        let currentValue = this.state.value;
        currentValue?.selected?.splice(currentValue?.selected?.indexOf(option), 1);
        this.setStateValue(currentValue);
    }

    removeOptionFromStateByValue(value: any){
        let currentValue = this.state.value;
        //get the index of the given value
        let index = currentValue?.selected?.findIndex((item: any) => item.value == value);


        currentValue?.selected?.splice(index, 1);
        this.setStateValue(currentValue);
    }

    componentDidMount() {//every filter needs to add itself to the FiltersManagers list of filters

        if(this.filtersManager){
            this.filtersManager.addFilter(this);
        }

        let clearButton = document.querySelector("#delta-filters-clear-filter-link_" + this.id);

        clearButton?.addEventListener("click", () => {
            this.clear(undefined, false);
        })

    }

    reset(cb: () => void){

        if(Object.keys(this.initialStateValue).length === 0){
            this.clearStateValue(() => {
                cb();
            });
        }
        else{
            this.setStateValue(this.initialStateValue, () => {
                cb();
            })
        }
    }

    clear(cb?: () => void, fullClear?: boolean) {
        this.setStateValue(JSON.parse(JSON.stringify(this.cleared)), () => {
            if(!fullClear){
                this.filtersManager?.handleApply();
            }
            if(cb)
                cb();
        });
    };

    applyFilter(): Record<string, string>{
        this.lastAppliedValue = JSON.parse(JSON.stringify(this.state.value));
        let params = this.getQueryParam();
        if(params){
            return params;
        }
        else{
            return {};
        }
    }

    abstract getQueryParam(): Record<string, string> | undefined;

}